import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';
import Device from 'src/app/services/device.model';
import DeviceParameterModel from 'src/app/services/device-parameter.model';
import QueryParamsModel from 'src/app/services/queryParams.model';
import { DeviceService } from 'src/app/services/device.service';
import { DeviceParameterService } from 'src/app/services/device-parameter.service';
import { QueryParamsService } from 'src/app/services/query-params.service';

@Component({
  selector: 'app-device-parameters',
  templateUrl: './device-parameters.component.html',
  styleUrls: ['./device-parameters.component.scss']
})
export class DeviceParametersComponent implements OnInit, OnDestroy {
  public devices: Array<Device> = [];
  public deviceParameters: Array<DeviceParameterModel> = [];
  public displayDeviceParameters: any[] = [];
  public filteredDeviceParameters: any[] = [];
  public paginatedDeviceParametersList: any[] = [];
  public unassignedDevices: Array<DeviceParameterModel> = [];
  public queryParams: Array<QueryParamsModel> = [];
  public allqpdata: any[] = [];

  public search: string = '';
  public submitButton: boolean = false;
  public updateButton: boolean = false;
  public deviceParametersDoubleClick: boolean = false;
  public enableDisablePageLoader: boolean = false;
  public listLoader: boolean = false;
  public isFiltering: boolean = false;
  public noList: boolean = false;
  public showForm: boolean = false;
  public formFieldCount: number = 0;
  public selectedDeviceId: string = '';
  public selectedDeviceNumber: string = '';

  public registerForm: FormGroup;
  public submitted = false;
  private searchTimeout: any;
  private scrollTimeout: any;
  private batchLoader: BatchLoader<any>;

  constructor(
    private toastrService: ToastrService,
    public formBuilder: FormBuilder,
    public deviceParameterService: DeviceParameterService,
    public deviceService: DeviceService,
    public queryParamsService: QueryParamsService,
    private locationStrategy: LocationStrategy,
    private previousRouteService: PreviousRouteService
  ) {
    this.registerForm = this.formBuilder.group({
      device_id: ['', Validators.required]
    });
    this.formFieldCount = 8;
  }

  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.enableDisablePageLoader = true;
      this.clear();
      this.search = '';
      this.isFiltering = false;
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());

      const [params, qps, unassigned, devs] = await Promise.all([
        this.deviceParameterService.getAllDeviceParams(),
        this.queryParamsService.getAllQueryParam(),
        this.deviceParameterService.getUnassignedDevices(),
        this.deviceService.getAllDevices()
      ]);

      this.deviceParameters = params || [];
      this.queryParams = qps || [];
      this.allqpdata = JSON.parse(JSON.stringify(this.queryParams));
      this.unassignedDevices = unassigned || [];
      this.devices = devs || [];

      this.displayDeviceParameters = this.getAllDeviceParamsByDeviceId(this.deviceParameters);
      this.initializeBatchLoader();
      this.batchLoader?.reset();
      await this.batchLoader?.loadNextBatch(true);
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.enableDisablePageLoader = false;
    }
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredDeviceParameters : this.displayDeviceParameters,
      (updatedList) => {
        this.paginatedDeviceParametersList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  public ngOnDestroy(): void {
    this.deviceParameterService.cancelOnGoingHttpRequests();
    this.queryParamsService.cancelOnGoingHttpRequests();
    this.deviceService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  public getAllDeviceParamsByDeviceId(deviceParameters: Array<DeviceParameterModel>) {
    let _deviceIds = deviceParameters.map(a => a.deviceNumber);
    let uniqueDevices = _deviceIds.filter((item, pos) => _deviceIds.indexOf(item) === pos);
    let display = [];
    for (let i = 0; i < uniqueDevices.length; i++) {
      let filteredDeviceParam = deviceParameters.filter(device => device.deviceNumber === uniqueDevices[i]);
      display.push({
        deviceNumber: uniqueDevices[i],
        params: filteredDeviceParam
      });
    }
    return display;
  }

  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.displayDeviceParameters.filter(group =>
          group.deviceNumber?.toLowerCase().includes(query) ||
          group.params.some((p: any) => p.qpName?.toLowerCase().includes(query))
        )
      : this.displayDeviceParameters;

    this.filteredDeviceParameters = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      this.noList = true;
      this.paginatedDeviceParametersList = [];
      return;
    }
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch();
    }, 300);
  }

  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
    this.deviceParametersDoubleClick = false;
    this.selectedDeviceId = '';
    this.selectedDeviceNumber = '';
    if (this.allqpdata && this.allqpdata.length > 0) {
      this.allqpdata.forEach(qp => qp.isAvailable = false);
    }
  }

  public openForm() {
    this.clear();
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
  }

  public getDeviceParamByNumber(deviceNumber: string) {
    this.showForm = true;
    this.submitButton = false;
    this.updateButton = true;
    this.selectedDeviceNumber = deviceNumber;

    const deviceParams = this.deviceParameters.filter(dp => dp && dp.deviceNumber === deviceNumber);
    if (deviceParams.length > 0) {
      this.selectedDeviceId = deviceParams[0].device_id;
      this.registerForm.patchValue({ device_id: this.selectedDeviceId });
    }

    for (let i = 0; i < this.allqpdata.length; i++) {
      const exist = deviceParams.some(dp => dp.qp_id === this.allqpdata[i].id);
      this.allqpdata[i].isAvailable = exist;
    }
  }

  public async addUpdateDeviceParam() {
    this.submitted = true;
    if (this.submitButton && this.registerForm.invalid) {
      return;
    }

    const selectedQPArr = this.allqpdata.filter(qp => qp.isAvailable);
    const selectedQPId = selectedQPArr.map(qp => qp.id);

    if (this.submitButton) {
      const deviceId = this.registerForm.value.device_id;
      const payload: any = {
        qp_id: selectedQPId,
        device_id: deviceId,
        updated_date: this.todayDate(),
        created_date: this.todayDate()
      };
      try {
        this.deviceParametersDoubleClick = true;
        await this.deviceParameterService.addDeviceParams(payload);
        this.toastrService.success('Device parameters created successfully', '', { timeOut: 2000 });
        await this.ngOnInit();
      } catch (error) {
        this.toastrService.error(error?.error?.message || error?.message || error);
        this.deviceParametersDoubleClick = false;
      }
    } else {
      const payload: any = {
        qp_id: selectedQPId,
        device_id: this.selectedDeviceId,
        updated_date: this.todayDate(),
        person_id: localStorage.personID,
        updated_name: localStorage.getItem('LoginUserName')
      };
      try {
        this.deviceParametersDoubleClick = true;
        await this.deviceParameterService.updateDeviceParams(payload);
        this.toastrService.success('Device parameters updated successfully', '', { timeOut: 2000 });
        await this.ngOnInit();
      } catch (error) {
        this.toastrService.error(error?.error?.message || error?.message || error);
        this.deviceParametersDoubleClick = false;
      }
    }
  }

  todayDate() {
    const d = new Date();
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()} ${d.getHours()}:${d.getMinutes()}:${d.getSeconds()}`;
  }
}
